import { Moment } from 'moment';
import { BusinessUnit, convertToMidnight, ReportType, Series } from '@cambridgeassessment/lib-ee-commons';
import actions from './actions';
import { SeriesActionTypes } from './types';
import {
  getActiveSeries as getActiveSeriesService,
  getSeriesComponents,
  getSeriesDetails,
  getSeriesResponse,
  updateBufferDate,
  updateDateByFieldName,
} from '../../service/series';
import { displayNotification } from '../notification/actions';
import { NotificationActionTypes } from '../notification/types';
import { buildReport, downloadReport, getSeriesReports, getSeriesWithRMAErrors } from '../../service/series/reporter';
import { sortSections } from './helpers';
import { buildReportName, downloadHandler } from '../../util/downloadHandler';
import { RootState } from '..';

type Dispatch = (action: SeriesActionTypes | NotificationActionTypes) => void;

export const getActiveSeriesData =
  () =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    const {
      series: { activeSeriesIds },
      userData: { businessUnit },
    } = getState();

    if (!activeSeriesIds && businessUnit?.id) {
      dispatch(actions.setIsLoading(true));
      await getActiveSeries(businessUnit.id)(dispatch);
      await getSeriesData(businessUnit.id)(dispatch);
    }

    dispatch(actions.setIsLoading(false));
  };

export const getSeriesData =
  (businessUnit: string, year?: number) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.getSeries());
      const requestData = await getSeriesResponse(businessUnit, year);
      dispatch(actions.setSeries(requestData));
    } catch (err) {
      dispatch(actions.setError());
      dispatch(displayNotification('Failed to get Series data'));
    }
  };

export const getDetailedSeries =
  (businessUnit: string, seriesId: number) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.getSeriesDetails());
      const requestData = await getSeriesDetails(businessUnit, seriesId);
      dispatch(actions.setSeriesDetails(requestData));
    } catch (err) {
      dispatch(displayNotification('Failed to get Series details'));
    }
  };

export const getSeriesComponentDates =
  (businessUnitId: BusinessUnit['id'], seriesId: Series['id']) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const result = await getSeriesComponents(businessUnitId, seriesId);
      dispatch(actions.setSeriesComponentsDates(result));
    } catch {
      dispatch(displayNotification('Failed to get series components'));
    }
  };

export const changeBufferDate =
  (seriesId: Series['_id'], buffer: Moment, submissionWindowName: string, field: string) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    try {
      const { seriesDetails } = getState().series;

      const midnightDate = convertToMidnight(buffer);
      const requestData = await updateBufferDate(seriesId, midnightDate, submissionWindowName, field);
      dispatch(
        actions.setSeriesDetails({
          ...requestData,
          marksEndDates: seriesDetails?.marksEndDates ?? {},
          workEndDates: seriesDetails?.workEndDates ?? {},
          componentSeriesStartDates: seriesDetails?.componentSeriesStartDates ?? [],
        }),
      );
    } catch (err) {
      dispatch(actions.setError());
      dispatch(displayNotification('Failed to update buffer'));
    }
  };

export const downloadReportAction =
  (
    businessUnitId: BusinessUnit['id'],
    series: { id: Series['id']; name: Series['name'] },
    reportType: ReportType,
    reportIndex: number,
  ) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.addReportAsBeingGenerated(reportIndex));
      let reportData;
      let reportName;
      if (reportType === ReportType.RMA_ERRORS) {
        reportData = await buildReport(businessUnitId, series, reportType);
        reportName = `All RM file submission failures for ${series.name} series.csv`;
      } else {
        reportData = await downloadReport(businessUnitId, series, reportType);
        reportName = buildReportName(reportType, series.name);
      }
      downloadHandler(reportData, reportName);
    } catch (error) {
      dispatch(displayNotification('Failed to download report'));
    } finally {
      dispatch(actions.removeReportAsBeingGenerated(reportIndex));
    }
  };

export const resetReportsBeingGenerated =
  () =>
  (dispatch: Dispatch): void =>
    dispatch(actions.resetReportsGeneratedList());

export const getSeriesReportsData =
  (businessUnitId: BusinessUnit['id'], reportType: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      if (reportType === ReportType.TRAINING) {
        const requestData = await getSeriesReports(businessUnitId, reportType);
        dispatch(actions.setSeriesTrainingReports(sortSections(requestData)));
      } else {
        const submittedExamined = await getSeriesReports(businessUnitId, ReportType.SUBMITTED_EXAMINED);
        const notSubmittedExamined = await getSeriesReports(businessUnitId, ReportType.NOT_SUBMITTED_EXAMINED);
        const submittedModerated = await getSeriesReports(businessUnitId, ReportType.SUBMITTED_MODERATED);
        const notSubmittedModerated = await getSeriesReports(businessUnitId, ReportType.NOT_SUBMITTED_MODERATED);
        const rmaErrors = await getSeriesWithRMAErrors(businessUnitId);

        dispatch(
          actions.setSeriesSubmissionReports(
            sortSections([
              ...submittedExamined,
              ...notSubmittedExamined,
              ...submittedModerated,
              ...notSubmittedModerated,
              ...rmaErrors,
            ]),
          ),
        );
      }
    } catch (err) {
      dispatch(actions.setError());
      dispatch(displayNotification('Failed to get Series reports data'));
    }
  };

export const getActiveSeries =
  (businessUnitId: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const { active, future } = await getActiveSeriesService(businessUnitId);
      dispatch(actions.setActiveSeriesIds(active?.map((series) => series.id) ?? []));
      dispatch(actions.setFutureSeriesIds(future?.map((series) => series.id) ?? []));
    } catch (e) {
      dispatch(displayNotification('Failed to get series data'));
    }
  };

export const updateSeriesDateByFieldName =
  (seriesId: Series['_id'], date: Moment, field: string) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    try {
      const { seriesDetails } = getState().series;

      const requestData = await updateDateByFieldName(seriesId, date.toISOString(), field);
      dispatch(
        actions.setSeriesDetails({
          ...requestData,
          marksEndDates: seriesDetails?.marksEndDates ?? {},
          workEndDates: seriesDetails?.workEndDates ?? {},
          componentSeriesStartDates: seriesDetails?.componentSeriesStartDates ?? [],
        }),
      );
    } catch (err) {
      dispatch(actions.setError());
      dispatch(displayNotification('Failed to update buffer'));
    }
  };

export const resetSubmissionReportRequest =
  () =>
  (dispatch: Dispatch): void => {
    dispatch(actions.setSubmissionReportHasLoaded(false));
    dispatch(actions.setSubmissionReportIsLoading(false));
    dispatch(actions.setSubmissionReportIndex());
  };

export default {
  getSeriesData,
  getDetailedSeries,
  changeBufferDate,
  getSeriesReportsData,
  downloadReportAction,
  resetSubmissionReportRequest,
  resetReportsBeingGenerated,
  getActiveSeries,
  getSeriesComponentDates,
  updateSeriesDateByFieldName,
};
